/**
 * CSV Template Download
 *
 * Generates a single-row CSV template with importable column headers
 * driven by columnConfig, and triggers a browser download.
 *
 * Consumed by `useArTableExportActions` ("Download Template (CSV)"), which hands
 * over the layout element's raw `columnConfig`: `undefined` is every schema
 * column, `[]` is none and downloads nothing (SYD-2279).
 */

import type { FieldType, UIArTableColumnOverride, CompositeFieldType } from '#types/System.types';
import type { ArTableType } from '#types/System.Artifacts.types';
import { flattenColumnsForCsv } from './flattenColumnsForCsv';

function escapeCsvField(value: string): string {
	if (value.includes('"') || value.includes(',') || value.includes('\n') || value.includes('\r')) {
		return `"${value.replace(/"/g, '""')}"`;
	}
	return value;
}

function downloadTemplateCsv(
	schema: ArTableType,
	fieldTypes: Record<string, FieldType>,
	columnConfig: UIArTableColumnOverride[] | undefined,
	compositeFieldTypes: CompositeFieldType[],
): void {
	const flatColumns = flattenColumnsForCsv(schema, fieldTypes, columnConfig, compositeFieldTypes);
	const importableHeaders = flatColumns
		.filter((col) => col.importable)
		.map((col) => escapeCsvField(col.header));

	if (importableHeaders.length === 0) return;

	const csvContent = importableHeaders.join(',') + '\r\n';
	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);

	const sanitize = (str: string) => str.replace(/[^a-zA-Z0-9-_ ]/g, '-').trim();
	const tableLabel = sanitize(schema.description?.shortLabel ?? 'table') || 'table';
	const filename = `${tableLabel}-template.csv`;

	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.click();

	URL.revokeObjectURL(url);
}

export { downloadTemplateCsv };
